import { useEffect, useRef, useCallback, useState } from "react";
import type { WSMessage } from "../lib/types";
import { eventBus } from "../lib/eventBus";
import { getAuthToken } from "../lib/api";

type MessageHandler = (msg: WSMessage) => void;

/**
 * Desde el 6-oct-2026 el servidor no abre /ws sin un ticket de un solo uso
 * (server/lib/ticketsWs.js): se pide con el código de acceso a
 * POST /api/sorv/ws-ticket y vale 30 s. Antes /ws hablaba con cualquiera que
 * llegara desde internet.
 *
 * Los reintentos no pueden gastar los intentos del límite de fallos del
 * servidor (20 respuestas 401 por IP en 15 min, y luego ni el código bueno
 * pasa): sin código guardado no se pide ticket, y si el servidor lo niega se
 * espera un minuto en vez de los 2 s de una caída normal. Un 401 además borra
 * el código y vuelve al login (lib/accesoPerdido.ts, que vigila todo fetch):
 * al minuto siguiente ya no hay código y no sale nada. Un 429 o un 503 no
 * borran nada; se vuelve a pedir al minuto.
 */
const REINTENTO_CAIDA_MS = 2_000;
const REINTENTO_RED_MS = 5_000;
const REINTENTO_SIN_ACCESO_MS = 60_000;

type Ticket = { ticket: string } | { esperaMs: number };

async function pedirTicket(): Promise<Ticket> {
  const token = getAuthToken();
  if (!token) return { esperaMs: REINTENTO_SIN_ACCESO_MS };
  try {
    const res = await fetch("/api/sorv/ws-ticket", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { esperaMs: REINTENTO_SIN_ACCESO_MS };
    const cuerpo = (await res.json()) as { ticket?: unknown };
    return typeof cuerpo.ticket === "string" && cuerpo.ticket !== ""
      ? { ticket: cuerpo.ticket }
      : { esperaMs: REINTENTO_SIN_ACCESO_MS };
  } catch {
    return { esperaMs: REINTENTO_RED_MS };
  }
}

export function useWebSocket(onMessage: MessageHandler) {
  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<MessageHandler>(onMessage);
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const mountedRef = useRef(true);
  // Pedir el ticket es asíncrono: si el componente se desmonta y se vuelve a
  // montar mientras tanto (StrictMode lo hace), la petición vieja no puede
  // abrir un segundo socket.
  const generacion = useRef(0);

  handlersRef.current = onMessage;

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;
    const estado = wsRef.current?.readyState;
    if (estado === WebSocket.OPEN || estado === WebSocket.CONNECTING) return;

    const mia = generacion.current;
    const programar = (ms: number) => {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = setTimeout(() => {
        void connect();
      }, ms);
    };

    const r = await pedirTicket();
    if (!mountedRef.current || mia !== generacion.current) return;
    if ("esperaMs" in r) {
      programar(r.esperaMs);
      return;
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    const ws = new WebSocket(`${protocol}//${host}/ws?ticket=${encodeURIComponent(r.ticket)}`);

    ws.onopen = () => {
      if (mountedRef.current) {
        setConnected(true);
        eventBus.setConnected(true);
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as WSMessage;
        handlersRef.current(msg);
      } catch {
        // ignore malformed messages
      }
    };

    ws.onclose = () => {
      if (mountedRef.current && mia === generacion.current) {
        setConnected(false);
        eventBus.setConnected(false);
        programar(REINTENTO_CAIDA_MS);
      }
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    generacion.current += 1;
    void connect();
    return () => {
      mountedRef.current = false;
      generacion.current += 1;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { connected };
}
